import * as React from "react";
import { ProfileHead } from "pages/svgs/profile";
import { useTranslation } from "react-i18next";
import { useContext, useState, useRef, useEffect } from "react";
import AuthContext from "contexts/AuthContext";
import ConfirmationModal from "./ConfirmationModal";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  cancelAppointment as cancel,
  updateAppointment,
} from "../appointments/service";
import { useNavigate } from "react-router-dom";
import { useCurrentLocation } from "lib/utils";

// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const getTimezoneForState = (state) => {
  const timezoneMap = {
    CO: "America/Denver", // MDT
    AR: "America/Chicago", // CDT
    NJ: "America/New_York", // EDT
    PA: "America/New_York", // EDT
  };
  return timezoneMap[state] || dayjs.tz.guess();
};

const EllipsisMenu = ({ onEdit, onExtend, onNoShow, onCancel }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        className="p-2 rounded-full hover:bg-gray-200"
        onClick={() => setMenuOpen((open) => !open)}
        aria-label="Appointment options"
      >
        <span className="text-2xl">⋯</span>
      </button>
      {menuOpen && (
        <div
          ref={menuRef}
          className={`absolute w-48 bg-white border border-gray-300 rounded-lg shadow-lg z-10
            bottom-full right-4`}
        >
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            onClick={() => {
              setMenuOpen(false);
              onEdit();
            }}
          >
            Edit
          </button>
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            onClick={() => {
              setMenuOpen(false);
              onExtend();
            }}
          >
            Extend
          </button>
          {/* <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            onClick={() => {
              setMenuOpen(false);
              onNoShow();
            }}
          >
            No show
          </button> */}
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            onClick={() => {
              setMenuOpen(false);
              onCancel();
            }}
          >
            Cancel
          </button>
          <button
            className="block w-full text-left px-4 py-2 hover:bg-gray-100"
            onClick={() => {
              setMenuOpen(false);
              onNoShow();
            }}
          >
            No Show
          </button>
        </div>
      )}
    </div>
  );
};

const ScheduleCard = ({
  id,
  appointment,
  setRescheduledAppointment,
  setExtendedAppointment,
  time,
  name,
  duration,
  originalEstimatedWeight,
  weight,
  first_name,
  last_name,
  isPast,
  takebackId,
  noMenu = false,
  address = "425-403 E 10th Ave, Roselle, NJ 07203",
  storeLocation = "LEGO Store Colorado Mills, 14500 W Colfax Ave, Lakewood, CO 80401",
  onDataChange,
}) => {
  // translation
  const { t } = useTranslation();
  const navigate = useNavigate();
  const unit = `${t("scale.weight")}`;
  const { authTokens } = useContext(AuthContext);
  const accessToken = authTokens.access_token;

  // Get current location settings
  const { location: currentLocation } = useCurrentLocation();

  // Modal state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showNoShowModal, setShowNoShowModal] = useState(false);

  const handleCancel = () => setShowCancelModal(true);
  const handleNoShow = () => setShowNoShowModal(true);

  const confirmCancel = () => {
    setShowCancelModal(false);
    cancel(id, `appointmentCancelledIntentAt`, accessToken, takebackId).then(
      () => {
        if (onDataChange) {
          onDataChange();
        }
      }
    );
  };
  const confirmNoShow = () => {
    setShowNoShowModal(false);
    updateAppointment(
      id,
      { arrivalStatus: "no-show" },
      `appointmentMissedIntentAt`,
      accessToken,
      takebackId
    ).then(() => {
      if (onDataChange) {
        onDataChange();
      }
    });
  };

  // Determine background color based on appointment time relative to current time
  const timezone = getTimezoneForState(currentLocation.state);

  const hasStateTimezone =
    currentLocation && currentLocation.state
      ? ["CO", "AR", "NJ", "PA"].some((item) =>
          currentLocation.state.toUpperCase().includes(item)
        )
      : false;

  let now, appointmentEnd, appointmentStart;

  if (hasStateTimezone) {
    now = dayjs().tz(timezone);
    appointmentEnd = dayjs(appointment.end_time).tz(timezone);
    appointmentStart = dayjs(appointment.time).tz(timezone);
  } else {
    now = dayjs();
    appointmentEnd = dayjs(appointment.end_time);
    appointmentStart = dayjs(appointment.time);
  }

  let profileHeadBackground = "#E4E4E4";

  if (appointment.arrival_status === "no-show") {
    profileHeadBackground = "#E3000B";
  }

  if (appointment.arrival_status === "arrived") {
    profileHeadBackground = "#C0DFC3";
  }

  if (
    now.isBetween(appointmentStart, appointmentEnd) &&
    appointment.arrival_status === "arrived"
  ) {
    // Current time is during appointment - blue
    profileHeadBackground = "#BBDCF5";
  }

  return (
    <>
      <div
        className={`dark:bg-gray-800 py-4 flex items-start justify-between cursor-pointer`}
        onClick={() => {
          navigate(
            t("urls.store_schedule") + "/" + appointment.sys_id + "?flow=kiosk"
          );
        }}
      >
        <div className="flex flex-1 items-start">
          <div
            className={`bg-white px-4 py-3 rounded-lg w-full border-[#D2D2D2] shadow-md border-1  ${
              appointment.duration < 40
                ? ""
                : appointment.duration <= 60
                  ? "pb-20"
                  : "pb-40"
            }`}
          >
            <div className="flex-1 w-full flex">
              <div
                className={`mr-4 my-auto bg-[${profileHeadBackground}] rounded-full p-2`}
              >
                <ProfileHead />
              </div>
              <div className="cursor-pointer">
                <div>
                  <div className="text-[#000000]">{name}</div>
                  <div className="text-[#2C2C2C] dark:text-gray-400">
                    <small>
                      Estimated: {originalEstimatedWeight}
                      {unit}
                    </small>
                  </div>
                </div>
              </div>
              {!noMenu && (
                <div
                  className="flex ml-auto mb-auto relative"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <EllipsisMenu
                    onEdit={() => {
                      setRescheduledAppointment({
                        id,
                        time,
                        first_name,
                        last_name,
                      });
                    }}
                    onExtend={() => {
                      setExtendedAppointment({
                        id,
                        time,
                        first_name,
                        last_name,
                      });
                    }}
                    onNoShow={handleNoShow}
                    onCancel={handleCancel}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <ConfirmationModal
        open={showNoShowModal}
        title="Set as no show"
        description="If you mark this appointment as a no show, the time slot will be freed up and the seller will get an email notification."
        confirmLabel="Confirm No Show"
        cancelLabel="Cancel"
        onCancel={() => setShowNoShowModal(false)}
        onConfirm={confirmNoShow}
      />
      <ConfirmationModal
        open={showCancelModal}
        title="Cancel appointment"
        description="If you cancel this appointment, the time slot will be freed up and the seller will get an email notification."
        confirmLabel="Confirm Cancel"
        cancelLabel="Back"
        onCancel={() => setShowCancelModal(false)}
        onConfirm={confirmCancel}
      />
    </>
  );
};

export default ScheduleCard;
